// MCP Security — Sessions page.
//
// One row per session_fp. Tool-call mix, taint, anomaly code, and
// status at a glance; clicking opens a right-anchored drawer with
// the last 10 turns + Revoke action.
//
// Wired 2026-10-11 to the MCP-UX routes:
//   GET /mcp/sites/:site_uid/sessions
//   GET /mcp/sites/:site_uid/sessions/:fp/timeline
//   POST /mcp/sites/:site_uid/sessions/:fp/revoke

import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Stack, Typography, Alert, Chip, Button, LinearProgress,
} from "@mui/material";
import { Workflow } from "lucide-react";
import copy from "copy-to-clipboard";

import axios from "../../../../../utils/axios/v1/userAxios";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";

import WafEventDrawerShell from "../../detection/WafEventDrawerShell";
import SmartParseDrawerHeader from "../../detection/v3/SmartParseDrawerHeader";
import { SmartParseSectionCard, spLh } from "../../detection/v3/smartParseDrawerUi";

import tokens from "./_shared/tokens";
import MetricCard from "./_shared/MetricCard";
import OutcomePill from "./_shared/OutcomePill";
import useMcpQuery from "./_shared/useMcpQuery";
import {
  dangerButtonStyle,
  secondaryButtonStyle,
  TEXT_PRIMARY,
  BORDER_COLOR,
} from "./_shared/buttonStyles";

const TABS = [
  { k: "all", label: "All" },
  { k: "tainted", label: "Tainted" },
  { k: "anomaly", label: "Anomaly" },
  { k: "revoked", label: "Revoked" },
];

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!d.getTime()) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function fmtDuration(from, to) {
  if (!from || !to) return "—";
  const f = new Date(from).getTime();
  const t = new Date(to).getTime();
  if (!f || !t) return "—";
  const sec = Math.max(0, Math.round((t - f) / 1000));
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

function SessionDrawer({ open, siteUid, session, onClose, onRevoke, revoked }) {
  const path = session && siteUid
    ? `mcp/sites/${siteUid}/sessions/${session.session_fp}/timeline?limit=10`
    : null;
  const { data: timelineResp, error: tlError, loading: tlLoading } = useMcpQuery(path, { skip: !path });

  const handleCopy = (value) => {
    if (!value || value === "—") return;
    copy(String(value));
  };

  if (!session) return null;

  const turns = (timelineResp?.data || []).map((t, i) => ({
    n: i + 1,
    tool: t.tool || t.tool_name || "—",
    args: typeof t.args === "string" ? t.args : t.args ? JSON.stringify(t.args) : "—",
    time: fmtTime(t.ts || t.time),
    write: !!t.write,
    outcome: t.outcome || "pass",
    hits: t.hits || null,
    taintPin: !!t.taint_pin,
  }));

  const isRevoked = !!(revoked || session.revoked);
  const agentLabel = session.agent_display_name || session.agent_id || "unknown agent";

  const header = (
    <SmartParseDrawerHeader
      embedded
      title="MCP Session"
      eventId={session.session_fp}
      method={isRevoked ? "revoked" : "active"}
      path={agentLabel}
      onCopyEventId={handleCopy}
    />
  );

  const hero = (
    <Stack spacing={1.5} sx={{ pt: 1 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={isRevoked ? "revoked" : "active"}
          sx={{
            height: 24, fontSize: 11.5, borderRadius: "6px",
            backgroundColor: isRevoked ? "#FBE8E8" : "#E6F4EC",
            color: isRevoked ? "#8E1F1F" : "#1E6B3F",
            border: `1px solid ${isRevoked ? "#E8B8B8" : "#A5D5B9"}`,
          }}
        />
        {session.tainted && (
          <Chip
            size="small"
            label={`Tainted${session.taint_reasons?.length ? ` · ${session.taint_reasons[0]}` : ""}`}
            sx={{
              height: 24, fontSize: 11.5, borderRadius: "6px",
              backgroundColor: tokens.warning.bg,
              color: tokens.secondary[500],
              border: `1px solid ${BORDER_COLOR}`,
            }}
          />
        )}
        {session.anomaly && (
          <Chip
            size="small"
            label={session.anomaly}
            sx={{
              height: 24, fontSize: 11.5, borderRadius: "6px",
              backgroundColor: tokens.danger.bg,
              color: tokens.danger[600],
              border: `1px solid ${BORDER_COLOR}`,
              fontFamily: tokens.fontMono,
            }}
          />
        )}
        <Chip size="small" variant="outlined" label={`${session.calls || 0} calls`}
          sx={{ height: 24, fontSize: 11.5, borderRadius: "6px", borderColor: BORDER_COLOR, fontFamily: tokens.fontMono }} />
        {/* MCP_ROADMAP_UI_2026_11_03 — session identity details */}
        {session.binding && (
          <Chip size="small" variant="outlined"
            label={session.binding === "principal" ? "bound to verified principal" : "bound to TLS client + UA"}
            sx={{ height: 24, fontSize: 11.5, borderRadius: "6px", borderColor: BORDER_COLOR }} />
        )}
        {session.key_source && (
          <Chip size="small" variant="outlined"
            label={session.key_source === "sid" ? "Mcp-Session-Id" : "per credential (no session id)"}
            sx={{ height: 24, fontSize: 11.5, borderRadius: "6px", borderColor: BORDER_COLOR }} />
        )}
        {(session.regions || (session.region ? [session.region] : [])).map((r) => (
          <Chip key={r} size="small" variant="outlined" label={r}
            sx={{ height: 24, fontSize: 11.5, borderRadius: "6px", borderColor: BORDER_COLOR, fontFamily: tokens.fontMono }} />
        ))}
      </Stack>
      <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
        Revoking blocks only this session for 15 minutes; other sessions from the same network or agent are not affected. To stop an agent everywhere, block it on the Agents page.
      </Typography>
    </Stack>
  );

  const footerLeft = (
    <Typography sx={{ fontSize: 12, lineHeight: spLh.px21, color: tokens.neutral[700], wordBreak: "break-word" }}>
      Revoking writes a tombstone. Later requests under this fp return 401 for 15 min.
    </Typography>
  );

  const footerActions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Button onClick={onClose} sx={secondaryButtonStyle}>Close</Button>
      {isRevoked ? (
        <Typography sx={{ fontSize: 13, color: tokens.danger[600], px: 1 }}>Revoked</Typography>
      ) : (
        <Button sx={dangerButtonStyle} onClick={onRevoke}>
          Revoke session
        </Button>
      )}
    </Stack>
  );

  return (
    <WafEventDrawerShell
      open={!!open}
      onClose={onClose}
      title="MCP Session"
      headerVariant="hero"
      headerContent={header}
      heroContent={hero}
      loading={tlLoading}
      error={tlError || null}
      footerLeft={footerLeft}
      footerActions={footerActions}
    >
      <SmartParseSectionCard icon={Workflow} title={`Last ${turns.length} turns`}>
        {!tlLoading && !tlError && turns.length === 0 && (
          <Typography sx={{ fontSize: 13, color: tokens.neutral[700] }}>
            No turns recorded for this session yet.
          </Typography>
        )}
        <Stack spacing={0}>
          {turns.map((t) => (
            <Box key={t.n} sx={{ display: "grid", gridTemplateColumns: "28px 1fr", gap: 1 }}>
              <Stack alignItems="center">
                <Box sx={{ width: 24, height: 24, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 11, fontFamily: tokens.fontMono, fontWeight: 500, background: "#fff", color: TEXT_PRIMARY, border: `1px solid ${BORDER_COLOR}` }}>
                  {t.n}
                </Box>
                <Box sx={{ flex: 1, width: 1, background: BORDER_COLOR, minHeight: 12 }} />
              </Stack>
              <Box sx={{ pb: 1.5 }}>
                <Box sx={{ border: `1px solid ${BORDER_COLOR}`, borderRadius: "8px", p: 1.25 }}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <code style={{ fontFamily: tokens.fontMono, fontSize: 13, fontWeight: 500 }}>{t.tool}</code>
                    {t.write && (
                      <span style={{ fontSize: 10, padding: "0 6px", borderRadius: 999, background: tokens.warning.bg, color: tokens.secondary[500] }}>write</span>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 11, color: tokens.neutral[700] }}>{t.time}</Typography>
                    <OutcomePill outcome={t.outcome} variant="span" />
                  </Stack>
                  {t.args && t.args !== "—" && (
                    <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.neutral[800], mt: 0.5, wordBreak: "break-word" }}>
                      {t.args}
                    </Typography>
                  )}
                  {t.hits && t.hits.length ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75 }}>
                      {t.hits.map((h) => (
                        <span key={h} style={{ fontFamily: tokens.fontMono, fontSize: 10, padding: "1px 7px", borderRadius: 999, background: tokens.danger.bg, color: tokens.danger[600] }}>
                          {h}
                        </span>
                      ))}
                    </Stack>
                  ) : null}
                  {t.taintPin && (
                    <Typography sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1, fontSize: 12, fontWeight: 500, color: tokens.secondary[500] }}>
                      Taint set on this turn.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
      </SmartParseSectionCard>
    </WafEventDrawerShell>
  );
}

export default function McpSessions() {
  const { effectiveSiteUid } = useGlobalSite();
  const [tab, setTab] = useState("all");
  const [drawerSession, setDrawerSession] = useState(null);
  const [revokedFps, setRevokedFps] = useState(new Set());
  const [revokeErr, setRevokeErr] = useState(null);

  const path = effectiveSiteUid ? `mcp/sites/${effectiveSiteUid}/sessions?limit=200` : null;
  const { data: resp, error, loading, refetch } = useMcpQuery(path, { skip: !path });
  const sessions = resp?.data || [];

  const rows = useMemo(() => {
    let r = sessions.map((s) => ({
      ...s,
      revoked: s.revoked || revokedFps.has(s.session_fp),
    }));
    if (tab === "tainted") r = r.filter((s) => s.tainted);
    else if (tab === "anomaly") r = r.filter((s) => s.anomaly);
    else if (tab === "revoked") r = r.filter((s) => s.revoked);
    return r;
  }, [sessions, tab, revokedFps]);

  const metrics = useMemo(() => {
    return [
      { label: "Sessions", value: String(sessions.length), sub: "live via Redis SCAN" },
      { label: "Tainted", value: String(sessions.filter((s) => s.tainted).length), sub: "untrusted content pinned", fg: tokens.secondary[500] },
      { label: "Anomaly", value: String(sessions.filter((s) => s.anomaly).length), sub: "anomaly signatures", fg: tokens.danger[600] },
      { label: "Revoked", value: String(sessions.filter((s) => s.revoked || revokedFps.has(s.session_fp)).length), sub: "operator tombstones" },
    ];
  }, [sessions, revokedFps]);

  const onRevoke = async (fp) => {
    if (!effectiveSiteUid) return;
    try {
      setRevokeErr(null);
      await axios.post(`mcp/sites/${effectiveSiteUid}/sessions/${fp}/revoke`, {});
      setRevokedFps((prev) => {
        const next = new Set(prev);
        next.add(fp);
        return next;
      });
      refetch();
    } catch (e) {
      setRevokeErr(e?.response?.data?.message || e.message || "revoke failed");
    }
  };

  return (
    <>
      <Helmet title="MCP Security — Sessions" />
      <Stack spacing={3} sx={{ p: 6 }}>
        <V2PageHeader
          title="MCP Security — Sessions"
          description="One row per MCP session (Mcp-Session-Id, or credential for stateless clients). Attacks span turns — open a session to see the full chain."
        />
        <SiteScopeGate mode="any">
          {() => (
            <>
              {!effectiveSiteUid && <Alert severity="info">Select a site from the header to see sessions.</Alert>}
              {error && <Alert severity="error">{error}</Alert>}
              {revokeErr && <Alert severity="warning">{revokeErr}</Alert>}

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.5 }}>
                {metrics.map((m) => (
                  <MetricCard key={m.label} label={m.label} value={m.value} sub={m.sub} />
                ))}
              </Box>

              <Box sx={{ background: "#fff", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 1.75, borderBottom: `1px solid ${tokens.neutral[300]}` }}>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: tokens.neutral[1000] }}>Sessions</Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                      One row per MCP session (Mcp-Session-Id, or credential for stateless clients). Attacks span turns; open a session to see the full chain.
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
                    {TABS.map((t) => (
                      <Box
                        key={t.k}
                        onClick={() => setTab(t.k)}
                        sx={{
                          height: 34, px: 1.5, display: "grid", placeItems: "center",
                          fontSize: 13, cursor: "pointer",
                          background: tab === t.k ? tokens.primaryIndigo : "#fff",
                          color: tab === t.k ? "#fff" : tokens.neutral[800],
                        }}
                      >
                        {t.label}
                      </Box>
                    ))}
                  </Box>
                </Stack>

                {loading && <LinearProgress />}

                <Box sx={{ overflowX: "auto" }}>
                  <Box sx={{ minWidth: 1180 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "90px 160px 80px 80px 80px 70px minmax(200px,1.4fr) 90px 140px 90px",
                        gap: "0 12px",
                        px: 2, py: 1.25,
                        background: tokens.neutral[50],
                        borderBottom: `1px solid ${tokens.neutral[300]}`,
                        fontSize: 11, fontWeight: 500, color: tokens.neutral[700],
                        textTransform: "uppercase", letterSpacing: "0.04em",
                      }}
                    >
                      <span>session_fp</span><span>Agent</span><span>First seen</span><span>Last seen</span>
                      <span>Duration</span><span>Calls</span><span>Tool mix</span><span>Taint</span>
                      <span>Anomaly</span><span>Status</span>
                    </Box>
                    {rows.length === 0 && !loading && (
                      <Box sx={{ p: 3, textAlign: "center", color: tokens.neutral[700], fontSize: 13 }}>
                        No sessions match this tab.
                      </Box>
                    )}
                    {rows.map((s) => (
                      <Box
                        key={s.session_fp}
                        onClick={() => setDrawerSession(s)}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "90px 160px 80px 80px 80px 70px minmax(200px,1.4fr) 90px 140px 90px",
                          gap: "0 12px",
                          px: 2, height: 44, alignItems: "center",
                          borderBottom: `1px solid ${tokens.neutral[150]}`,
                          fontSize: 13, cursor: "pointer",
                          background: s.tainted ? "rgba(255,219,67,0.08)" : "#fff",
                          "&:hover": { background: tokens.neutral[50] },
                        }}
                      >
                        <span style={{ fontFamily: tokens.fontMono, fontWeight: 500, color: tokens.primary[300] }}>
                          {s.session_fp ? s.session_fp.slice(0, 10) : "—"}
                        </span>
                        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {s.agent_display_name || s.agent_id || "—"}
                        </span>
                        <span style={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.neutral[700] }}>{fmtTime(s.first_seen)}</span>
                        <span style={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.neutral[700] }}>{fmtTime(s.last_seen)}</span>
                        <span style={{ fontFamily: tokens.fontMono, fontSize: 12 }}>{fmtDuration(s.first_seen, s.last_seen)}</span>
                        <span style={{ fontFamily: tokens.fontMono }}>{s.calls || 0}</span>
                        <span style={{ display: "flex", gap: 4, overflow: "hidden" }}>
                          {(s.tool_mix || []).slice(0, 3).map((m) => (
                            <span key={m} style={{ fontFamily: tokens.fontMono, fontSize: 10, padding: "1px 7px", borderRadius: 999, background: tokens.neutral[100], whiteSpace: "nowrap" }}>
                              {m}
                            </span>
                          ))}
                        </span>
                        <span>
                          {s.tainted && (
                            <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 999, background: tokens.warning.bg, color: tokens.secondary[500] }}>
                              Tainted
                            </span>
                          )}
                        </span>
                        <span style={{ fontFamily: tokens.fontMono, fontSize: 12 }}>{s.anomaly || "—"}</span>
                        <span>
                          <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 999, background: s.revoked ? "#FBE8E8" : "#E6F4EC", color: s.revoked ? "#8E1F1F" : "#1E6B3F" }}>
                            {s.revoked ? "revoked" : "active"}
                          </span>
                        </span>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </SiteScopeGate>
      </Stack>

      <SessionDrawer
        open={!!drawerSession}
        siteUid={effectiveSiteUid}
        session={drawerSession}
        onClose={() => setDrawerSession(null)}
        onRevoke={() => drawerSession && onRevoke(drawerSession.session_fp)}
        revoked={drawerSession && revokedFps.has(drawerSession.session_fp)}
      />
    </>
  );
}
